import { useEffect, useState, useCallback } from 'react';
import { Loader2, RotateCcw, AlertTriangle, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { extractProjectData, type ProjectExtractionResult } from '@/lib/llm';
import {
  fetchDocumentTextsForProject,
  computeProjectDelta,
  applyProjectDelta,
  loadCurrentProjectSnapshot,
  deltaIsEmpty,
  type RegenerateProgress,
  type ProjectDelta,
  type CurrentProjectSnapshot,
} from '@/lib/projectRegenerate';

type Phase = 'idle' | 'fetching' | 'analyzing' | 'review' | 'applying' | 'error';

interface RegenerateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  workspaceId?: string;
  onApplied: () => Promise<void> | void;
}

export function RegenerateProjectDialog({
  open,
  onOpenChange,
  projectId,
  workspaceId,
  onApplied,
}: RegenerateProjectDialogProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<RegenerateProgress | null>(null);
  const [snapshot, setSnapshot] = useState<CurrentProjectSnapshot | null>(null);
  const [extracted, setExtracted] = useState<ProjectExtractionResult | null>(null);
  const [delta, setDelta] = useState<ProjectDelta | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase('idle');
    setProgress(null);
    setSnapshot(null);
    setExtracted(null);
    setDelta(null);
    setError(null);
  }, []);

  // Reset state whenever the dialog opens so a previous run doesn't bleed
  // into the next. Triggered only on transition false → true.
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const startRegenerate = async () => {
    setError(null);
    setPhase('fetching');
    setProgress({ stage: 'downloading', completed: 0, total: 0 });
    try {
      // Snapshot current state in parallel with re-extracting docs so the
      // diff is ready the moment both finish.
      const snapshotPromise = loadCurrentProjectSnapshot(projectId);

      const texts = await fetchDocumentTextsForProject(projectId, (p) => setProgress(p));
      if (texts.length === 0) {
        setPhase('error');
        setError('This project has no documents to regenerate from.');
        return;
      }

      setPhase('analyzing');
      setProgress({ stage: 'analyzing', completed: 0, total: texts.length });
      const [snap, fresh] = await Promise.all([
        snapshotPromise,
        extractProjectData(texts, workspaceId),
      ]);
      if (!snap) {
        setPhase('error');
        setError("Couldn't load the current project state to compare against.");
        return;
      }

      const computed = computeProjectDelta(snap, fresh);
      setSnapshot(snap);
      setExtracted(fresh);
      setDelta(computed);
      setPhase('review');
    } catch (err) {
      console.error('Regenerate failed:', err);
      setPhase('error');
      setError(err instanceof Error ? err.message : 'Regeneration failed.');
    }
  };

  const handleApply = async () => {
    if (!snapshot || !extracted) return;
    setPhase('applying');
    setError(null);
    try {
      await applyProjectDelta(projectId, snapshot, extracted);
      await onApplied();
      onOpenChange(false);
    } catch (err) {
      console.error('Apply regenerate failed:', err);
      setPhase('review');
      setError(err instanceof Error ? err.message : 'Failed to apply changes.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Regenerate project from documents
          </DialogTitle>
          <DialogDescription>
            Re-run AI extraction on the documents already uploaded to this
            project. Review the proposed changes before applying — the
            current state stays untouched until you click <strong>Apply</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {phase === 'idle' && <IdleView onStart={startRegenerate} />}

          {(phase === 'fetching' || phase === 'analyzing') && progress && (
            <ProgressView progress={progress} />
          )}

          {phase === 'review' && delta && <DiffReview delta={delta} />}

          {phase === 'applying' && (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Applying changes…
            </div>
          )}

          {phase === 'error' && (
            <div className="py-8 text-center">
              <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
              <p className="text-sm text-gray-800 font-medium">Regeneration failed</p>
              <p className="text-sm text-gray-500 mt-1">{error}</p>
            </div>
          )}

          {error && phase === 'review' && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          {phase === 'idle' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}
          {phase === 'review' && delta && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Discard
              </Button>
              <Button onClick={handleApply} disabled={deltaIsEmpty(delta)}>
                <Check className="h-4 w-4 mr-2" />
                {deltaIsEmpty(delta) ? 'No changes to apply' : 'Apply changes'}
              </Button>
            </>
          )}
          {phase === 'error' && (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

function IdleView({ onStart }: { onStart: () => void }) {
  return (
    <div className="py-6 space-y-4">
      <p className="text-sm text-gray-700">
        We'll re-download each uploaded document, re-extract the text, and
        re-run AI analysis. You'll see a side-by-side summary of changes
        before anything is written to this project.
      </p>
      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
        <strong>Heads up:</strong> applying a regeneration replaces milestones,
        risks, KPIs, accomplishments, and priorities. Any KPI measurements
        you've manually added will be replaced by what the LLM extracts.
      </div>
      <Button onClick={onStart}>
        <RotateCcw className="h-4 w-4 mr-2" />
        Start regeneration
      </Button>
    </div>
  );
}

function ProgressView({ progress }: { progress: RegenerateProgress }) {
  const pct =
    progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
  const label =
    progress.stage === 'downloading'
      ? 'Downloading documents'
      : progress.stage === 'extracting'
      ? 'Extracting text'
      : progress.stage === 'analyzing'
      ? 'Running AI analysis'
      : 'Done';
  return (
    <div className="py-8 space-y-4">
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-ingesta-blue-600" />
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-900">{label}…</div>
          {progress.currentFilename && (
            <div className="text-xs text-gray-500 truncate">{progress.currentFilename}</div>
          )}
        </div>
        {progress.total > 0 && (
          <div className="text-xs text-gray-500 tabular-nums">
            {progress.completed} / {progress.total}
          </div>
        )}
      </div>
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-ingesta-blue-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function DiffReview({ delta }: { delta: ProjectDelta }) {
  if (deltaIsEmpty(delta)) {
    return (
      <div className="py-12 text-center">
        <Check className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
        <p className="text-sm text-gray-800 font-medium">No changes detected</p>
        <p className="text-sm text-gray-500 mt-1">
          The AI extraction matches the current project state.
        </p>
      </div>
    );
  }

  return (
    <div className="py-2 space-y-5">
      {/* Project meta */}
      {delta.meta.length > 0 && (
        <Section title="Project details" count={delta.meta.length}>
          <ul className="space-y-2">
            {delta.meta.map((c) => (
              <li key={c.field} className="text-sm">
                <span className="font-medium text-gray-700">{c.field}: </span>
                <DiffValue was={c.was} now={c.now} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Milestones */}
      {(delta.milestones.added.length > 0 ||
        delta.milestones.removed.length > 0 ||
        delta.milestones.changed.length > 0) && (
        <Section
          title="Milestones"
          count={
            delta.milestones.added.length +
            delta.milestones.removed.length +
            delta.milestones.changed.length
          }
        >
          {delta.milestones.added.map((m, i) => (
            <ChangeRow key={`add-${i}`} type="add" name={m.name} />
          ))}
          {delta.milestones.removed.map((m) => (
            <ChangeRow key={m.id} type="remove" name={m.name} />
          ))}
          {delta.milestones.changed.map((c, i) => (
            <ChangeRow
              key={`chg-${i}`}
              type="change"
              name={c.current.name}
              fields={c.fieldChanges}
            />
          ))}
        </Section>
      )}

      {/* Risks */}
      {(delta.risks.added.length > 0 ||
        delta.risks.removed.length > 0 ||
        delta.risks.changed.length > 0) && (
        <Section
          title="Risks"
          count={
            delta.risks.added.length +
            delta.risks.removed.length +
            delta.risks.changed.length
          }
        >
          {delta.risks.added.map((r, i) => (
            <ChangeRow key={`add-${i}`} type="add" name={r.summary} />
          ))}
          {delta.risks.removed.map((r) => (
            <ChangeRow key={r.id} type="remove" name={r.summary} />
          ))}
          {delta.risks.changed.map((c, i) => (
            <ChangeRow
              key={`chg-${i}`}
              type="change"
              name={c.current.summary}
              fields={c.fieldChanges}
            />
          ))}
        </Section>
      )}

      {/* KPIs */}
      {(delta.kpis.added.length > 0 ||
        delta.kpis.removed.length > 0 ||
        delta.kpis.changed.length > 0) && (
        <Section
          title="KPIs"
          count={
            delta.kpis.added.length +
            delta.kpis.removed.length +
            delta.kpis.changed.length
          }
        >
          {delta.kpis.added.map((k, i) => (
            <ChangeRow
              key={`add-${i}`}
              type="add"
              name={`${k.name} — ${k.history.length} historical point${k.history.length === 1 ? '' : 's'}`}
            />
          ))}
          {delta.kpis.removed.map((k) => (
            <ChangeRow key={k.id} type="remove" name={k.name} />
          ))}
          {delta.kpis.changed.map((c, i) => {
            const fields = [...c.fieldChanges];
            if (c.historyChange.current !== c.historyChange.extracted) {
              fields.push({
                field: 'History',
                was: `${c.historyChange.current} pts`,
                now: `${c.historyChange.extracted} pts`,
              });
            }
            return (
              <ChangeRow
                key={`chg-${i}`}
                type="change"
                name={c.current.name}
                fields={fields}
              />
            );
          })}
        </Section>
      )}

      {/* Accomplishments */}
      {(delta.accomplishments.added.length > 0 ||
        delta.accomplishments.removed.length > 0) && (
        <Section
          title="Recent wins"
          count={delta.accomplishments.added.length + delta.accomplishments.removed.length}
        >
          {delta.accomplishments.added.map((s, i) => (
            <ChangeRow key={`add-${i}`} type="add" name={s} />
          ))}
          {delta.accomplishments.removed.map((s) => (
            <ChangeRow key={s.id} type="remove" name={s.content} />
          ))}
        </Section>
      )}

      {/* Priorities */}
      {(delta.priorities.added.length > 0 ||
        delta.priorities.removed.length > 0) && (
        <Section
          title="This week's priorities"
          count={delta.priorities.added.length + delta.priorities.removed.length}
        >
          {delta.priorities.added.map((s, i) => (
            <ChangeRow key={`add-${i}`} type="add" name={s} />
          ))}
          {delta.priorities.removed.map((s) => (
            <ChangeRow key={s.id} type="remove" name={s.content} />
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
        {title}
        <span className="text-xs font-normal text-gray-500">
          ({count} change{count === 1 ? '' : 's'})
        </span>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function ChangeRow({
  type,
  name,
  fields,
}: {
  type: 'add' | 'remove' | 'change';
  name: string;
  fields?: { field: string; was: unknown; now: unknown }[];
}) {
  const badge =
    type === 'add' ? (
      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
        ADD
      </span>
    ) : type === 'remove' ? (
      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700">
        REMOVE
      </span>
    ) : (
      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
        CHANGE
      </span>
    );
  return (
    <div className="text-sm">
      <div className="flex items-start gap-2">
        {badge}
        <span className="flex-1 text-gray-800">{name}</span>
      </div>
      {fields && fields.length > 0 && (
        <ul className="mt-1 ml-12 space-y-0.5 text-xs">
          {fields.map((f) => (
            <li key={f.field}>
              <span className="text-gray-500">{f.field}: </span>
              <DiffValue was={f.was} now={f.now} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DiffValue({ was, now }: { was: unknown; now: unknown }) {
  return (
    <span className="text-xs">
      <span className="text-red-600 line-through mr-1">{formatValue(was)}</span>
      <span className="text-emerald-700 font-medium">→ {formatValue(now)}</span>
    </span>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string') return v;
  return String(v);
}

// Quiet ESLint about the unused X icon — kept for parity with other dialogs
// in case we want a close affordance later.
void X;
